import { ArrowRight, Info } from 'lucide-react';
import { useState } from 'react';
import type {
  Campaign,
  CampaignBookings,
  CampaignRecipientFilter,
  CampaignResendSegment,
  CampaignStats,
} from '../../api/types';
import { pct } from '../../lib/commsFormat';
import { formatTenantMoney } from '../../lib/tenantCurrency';
import type { LoadState } from '../../lib/useLoad';
import { LineChart } from '../analytics/charts/Charts';
import { useComms } from './context';
import { useLoad } from '../../lib/useLoad';

/**
 * The pieces of a campaign's Overview tab, each answering one question:
 * the figures strip (how did it do), What it did (how far down the funnel
 * people went), Didn't reach (who never got it, and why), and Hour by hour
 * (when people read it).
 *
 * Split from CampaignResults because that screen also owns the stop, resume,
 * review and recipient-table machinery; these are pure reads of the campaign
 * and its bookings, and each hands navigation back to it through callbacks.
 */

const share = (value: number, of: number) => (of > 0 ? value / of : 0);

/** A whole-number percentage for the strip, where a decimal is noise. */
const round = (fraction: number) => `${Math.round(fraction * 100)}%`;

/**
 * The figures strip. Each tile is one number and a line that says what kind of
 * number it is — "a trend, not exact" on opens is the caveat the old side
 * panel spent a paragraph on, said where the number is read.
 */
export function CampaignKpis({
  campaign,
  stats,
  bookings,
  days,
}: {
  campaign: Campaign;
  stats: CampaignStats;
  bookings: LoadState<CampaignBookings | null>;
  days: number;
}) {
  const isSms = campaign.channel === 'sms';
  const tiles: { label: string; value: string; sub: string; tone?: 'green' }[] = [
    {
      label: 'Sent',
      value: stats.sent.toLocaleString(),
      sub: stats.total > 0 ? `${round(share(stats.sent, stats.total))} of ${stats.total.toLocaleString()}` : '—',
    },
    isSms
      ? {
          label: 'Delivered',
          value: stats.delivered.toLocaleString(),
          sub: `${round(share(stats.delivered, stats.sent))} · confirmed by the carrier`,
        }
      : {
          label: 'Opened',
          value: stats.opened.toLocaleString(),
          sub: `${round(share(stats.opened, stats.sent))} · a trend, not exact`,
        },
    ...(campaign.trackClicks
      ? [
          {
            label: isSms ? 'Tapped a link' : 'Clicked',
            value: (stats.clicked ?? 0).toLocaleString(),
            sub: `${round(share(stats.clicked ?? 0, stats.sent))} · ${isSms ? 'previews left out' : 'scanners left out'}`,
          },
        ]
      : []),
    {
      label: 'Replied',
      value: (stats.replied ?? 0).toLocaleString(),
      sub: 'conversations in your inbox',
    },
  ];
  const data = bookings.data;
  if (campaign.startedAt) {
    // "Couldn't check" and "still reading" are never drawn as a zero — the
    // card under the funnel says why; here the tile just declines to answer.
    tiles.push({
      label: 'Booked',
      value: data?.checked ? data.bookings.toLocaleString() : '—',
      sub: !data
        ? bookings.error
          ? 'couldn’t check'
          : 'reading dispatch…'
        : data.checked
          ? `within ${days} days of receiving it`
          : 'couldn’t check',
      tone: data?.checked && data.bookings > 0 ? 'green' : undefined,
    });
    // Only for somebody dispatch lets see the company's money — null is "not
    // yours to see", not "none".
    if (data?.checked && data.revenue !== null) {
      tiles.push({
        label: 'Reservation value',
        value: formatTenantMoney(data.revenue, 0),
        sub: 'what followed, not proof',
      });
    }
  }
  return (
    <div className="cmg-kpis" data-card="kpis">
      {tiles.map((tile) => (
        <div key={tile.label} className="cmg-kpi">
          <div className="cstat-label">{tile.label}</div>
          <div className={`cmg-kpi-value${tile.tone ? ` ${tile.tone}` : ''}`}>{tile.value}</div>
          <div className="cmg-kpi-sub">{tile.sub}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * What it did: one funnel, each step a share of the people it was SENT to.
 *
 * The old funnel mixed outcomes with the ways a send fails — Recipients, Sent,
 * Opened, Bounced, Failed, Opted out on one scale — so the bar for "Opened"
 * sat beside the bar for "Failed" as if they were the same kind of fact. The
 * failures are their own panel now (CampaignReach), and this one only goes
 * down: sent, then what the people who got it did.
 */
export function CampaignOutcome({
  campaign,
  stats,
  bookings,
  mayFollowUp,
  onRecipients,
  onFollowUp,
}: {
  campaign: Campaign;
  stats: CampaignStats;
  bookings: CampaignBookings | null;
  mayFollowUp: boolean;
  onRecipients: (filter: CampaignRecipientFilter) => void;
  onFollowUp: (segment: CampaignResendSegment) => void;
}) {
  const isSms = campaign.channel === 'sms';
  const base = stats.sent;
  const rows: { label: string; value: number }[] = [
    { label: 'Sent', value: stats.sent },
    isSms ? { label: 'Delivered', value: stats.delivered } : { label: 'Opened', value: stats.opened },
    ...(campaign.trackClicks ? [{ label: isSms ? 'Tapped a link' : 'Clicked', value: stats.clicked ?? 0 }] : []),
    { label: 'Replied', value: stats.replied ?? 0 },
    // PEOPLE who booked rather than bookings, so the row is the same kind of
    // number as the ones above it.
    ...(bookings?.checked ? [{ label: 'Booked a trip', value: bookings.bookers }] : []),
  ];
  const unclicked = Math.max(0, stats.opened - (stats.clicked ?? 0));
  const finished = campaign.status === 'completed' || campaign.status === 'failed';
  return (
    <div className="cmg-card" data-card="outcome" style={{ flex: '1.4 1 360px', gap: 10, minWidth: 0 }}>
      <div>
        <div className="cmg-card-title">What it did</div>
        <div className="cmg-results-sub">
          Each step as a share of the {base.toLocaleString()} it was sent to
        </div>
      </div>
      {rows.map((row) => (
        <div key={row.label} className="cmg-funnel-row cmg-outcome-row">
          <span className="cmg-funnel-label">{row.label}</span>
          <span className="cmg-funnel-bar">
            <span
              style={{
                width: `${Math.round(share(row.value, base) * 100)}%`,
                background: 'var(--c-primary)',
              }}
            />
          </span>
          <span className="cmg-funnel-value">{row.value.toLocaleString()}</span>
          <span className="cmg-funnel-pct">{pct(share(row.value, base))}</span>
        </div>
      ))}
      <div className="cmg-outcome-links">
        {campaign.trackClicks && (stats.clicked ?? 0) > 0 && (
          <button type="button" className="cmg-link-btn" onClick={() => onRecipients('clicked')}>
            {isSms ? 'See who tapped' : 'See who clicked'}
            <ArrowRight size={12} />
          </button>
        )}
        {(stats.replied ?? 0) > 0 && (
          <button type="button" className="cmg-link-btn" onClick={() => onRecipients('replied')}>
            See who replied
            <ArrowRight size={12} />
          </button>
        )}
        {!isSms && finished && mayFollowUp && campaign.trackClicks && unclicked > 0 && (
          <button type="button" className="cmg-link-btn" onClick={() => onFollowUp('unclicked')}>
            Follow up with people who opened but didn’t click · {unclicked.toLocaleString()}
            <ArrowRight size={12} />
          </button>
        )}
      </div>
      <div className="cmg-hint" style={{ display: 'flex', gap: 6 }}>
        <Info size={13} style={{ flex: 'none', marginTop: 2 }} />
        <span>
          {isSms
            ? 'There is no open on SMS — nothing can tell you a text was read. Delivered is the carrier confirming it arrived; a few stay unconfirmed for hours and then land anyway.'
            : 'Opens come from a tracking pixel: image-blocking clients under-count, Apple and Gmail proxies over-count. Read the trend, not the exact number.'}
        </span>
      </div>
    </div>
  );
}

/**
 * Didn't reach: everybody on the campaign who never got it, by reason — and
 * the one number on Results that judges the words rather than the delivery,
 * the people who left after it. Each row opens the recipient list on exactly
 * those people.
 */
export function CampaignReach({
  campaign,
  stats,
  onRecipients,
}: {
  campaign: Campaign;
  stats: CampaignStats;
  onRecipients: (filter: CampaignRecipientFilter) => void;
}) {
  const isSms = campaign.channel === 'sms';
  const rows: { key: CampaignRecipientFilter; label: string; value: number; tone?: 'bad' }[] = [
    {
      key: 'bounced',
      label: isSms ? 'Bad number — clean before the next send' : 'Bounced — clean before the next send',
      value: stats.bounced,
      tone: 'bad',
    },
    { key: 'failed', label: 'Failed to send', value: stats.failed, tone: 'bad' },
    ...(isSms
      ? [
          {
            key: 'undelivered' as const,
            label: 'Sent, delivery never confirmed',
            value: Math.max(0, stats.sent - stats.delivered),
          },
        ]
      : []),
    { key: 'capped', label: 'Skipped · recently contacted', value: stats.capped ?? 0 },
    { key: 'suppressed', label: 'Skipped · already opted out', value: stats.suppressed },
    {
      key: 'bounced_before',
      label: isSms ? 'Skipped · a bad number before' : 'Skipped · bounced before',
      value: stats.bouncedBefore ?? 0,
    },
    { key: 'pending', label: 'Still to send', value: stats.pending },
  ];
  const shown = rows.filter((row) => row.value > 0);
  return (
    <div className="cmg-card" data-card="reach" style={{ flex: '1 1 300px', gap: 6, minWidth: 0 }}>
      <div className="cmg-card-title">Didn’t reach</div>
      {shown.length === 0 ? (
        <div className="cmg-results-sub">Everybody it was meant for was sent it.</div>
      ) : (
        shown.map((row) => (
          <div key={row.key} className="cmg-reach-row">
            <span className={`cmg-reach-dot${row.tone ? ` ${row.tone}` : ''}`} />
            <span style={{ flex: 1 }}>{row.label}</span>
            <b>{row.value.toLocaleString()}</b>
            <button type="button" className="cmg-link-btn" onClick={() => onRecipients(row.key)}>
              View
            </button>
          </div>
        ))
      )}
      {stats.bounced > 0 && !isSms && (
        <div className="cmg-hint">
          Bounces are matched from the reports that come back to the sending mailbox, so a few may
          be missing. Mailing a dead address again hurts the domain.
        </div>
      )}
      {isSms && stats.failed > 0 && (
        <div className="cmg-hint">
          A failure is usually about the number — carrier filtering, or a sender not registered for
          this traffic — rather than the people. They usually receive it once that is sorted.
        </div>
      )}
      <div className="cmg-reach-left">
        <div>
          Left after it:{' '}
          <b>
            {stats.unsubscribed.toLocaleString()} {isSms ? 'opted out' : 'unsubscribed'}
          </b>
          {stats.unsubscribed > 0 && (
            <button
              type="button"
              className="cmg-link-btn"
              style={{ marginLeft: 8 }}
              onClick={() => onRecipients('unsubscribed')}
            >
              View
            </button>
          )}
        </div>
        <div className="cmg-results-sub">
          {pct(share(stats.unsubscribed, stats.sent))} — the one number here that judges the words
          rather than the delivery.
        </div>
      </div>
    </div>
  );
}

/** The windows the hour-by-hour chart offers — the first day, or the first three. */
const ACTIVITY_WINDOWS = [
  { hours: 24, label: 'First day' },
  { hours: 72, label: 'First 3 days' },
];

/**
 * Opens (deliveries, on SMS) and clicks per hour since it started — when the
 * list reads its mail, which is the one thing worth knowing before choosing
 * the time for the next one. First events only, so one person reading it ten
 * times is one open.
 */
export function CampaignActivity({ campaign }: { campaign: Campaign }) {
  const { source } = useComms();
  const [hours, setHours] = useState(24);
  const isSms = campaign.channel === 'sms';
  const stats = campaign.stats;
  const load = useLoad(
    () => source.api.getCampaignActivity(campaign.id, hours),
    // Re-read as the counts move, so a campaign still sending draws its
    // newest hour without a press of Refresh.
    [source, campaign.id, hours, stats?.opened, stats?.delivered, stats?.clicked],
  );
  if (!campaign.startedAt) return null;
  const data = load.data;
  const primary = data ? (data.series[data.primary] ?? []) : [];
  const clicked = data?.series.clicked ?? [];
  const points = primary.map((value, index) => ({ label: `${index}h`, value }));
  const empty = data !== null && primary.every((value) => value === 0) && clicked.every((value) => value === 0);
  return (
    <div className="cmg-card" data-card="activity" style={{ gap: 8 }}>
      <div className="cmg-website-head">
        <div style={{ flex: 1 }}>
          <div className="cmg-card-title">{isSms ? 'Deliveries and taps, hour by hour' : 'Opens and clicks, hour by hour'}</div>
          <div className="cmg-results-sub">
            First {isSms ? 'confirmations' : 'opens'} per hour after it started — one person reading
            it twice counts once.
          </div>
        </div>
        <div className="cmg-filter-chips">
          {ACTIVITY_WINDOWS.map((window) => (
            <button
              key={window.hours}
              type="button"
              className={`cmg-filter-chip${hours === window.hours ? ' on' : ''}`}
              onClick={() => setHours(window.hours)}
            >
              {window.label}
            </button>
          ))}
        </div>
      </div>
      {load.error && !data ? (
        <div className="cmg-hint">Could not load the hour-by-hour figures.</div>
      ) : !data ? (
        <div className="cmg-chart-skeleton" />
      ) : empty ? (
        <div className="cmg-hint">
          Nothing to draw yet — {isSms ? 'no deliveries have been confirmed' : 'nobody has opened it'} in
          this window.
        </div>
      ) : (
        <LineChart
          points={points}
          height={190}
          color="var(--viz-1)"
          valueLabel={isSms ? 'Deliveries' : 'Opens'}
          format={(value) => value.toLocaleString()}
          extra={
            campaign.trackClicks
              ? { label: isSms ? 'Taps' : 'Clicks', color: 'var(--viz-2)', values: clicked }
              : undefined
          }
        />
      )}
    </div>
  );
}
